import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AvatarCardUser } from '../core/basic-user.model';
import { environment } from 'src/environments/environment';
import {
  ActionButton,
  computeActionButtons,
  computeUserMenuItems,
  NavigationItem,
  UserMenuItem
} from './navigation.config';
import { DeclarationsAuthService } from '../auth/declarations-auth.service';
import { UsersService } from '../core/users.service';
import { ThemeService } from '../core/theme.service';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'ad-sidebar-nav',
  imports: [RouterLink],
  template: `
    @if (loggedIn) {
      <aside class="sidebar">
        <!-- Logo and Brand -->
        <div class="sidebar-header">
          <a class="sidebar-brand d-flex align-items-center" [routerLink]="['/']">
            <img src="logo.png" style="width: 32px; height: 32px;" alt="Logo" class="me-2" />
            <span class="sidebar-brand-text">{{ appName }}</span>
          </a>
        </div>

        <!-- Action Buttons Below Header -->
        <div class="sidebar-actions">
          @for (action of actionButtons; track action.id) {
            @if (action.id === 'theme-toggle') {
              <!-- Theme Toggle -->
              <button class="sidebar-action" type="button" [attr.aria-label]="action.label" (click)="toggleTheme()">
                @if (isDarkMode) {
                  <span class="material-icons">dark_mode</span>
                } @else {
                  <span class="material-icons">light_mode</span>
                }
              </button>
            } @else if (action.id === 'search') {
              <!-- Search Button -->
              <button class="sidebar-action" type="button" [attr.aria-label]="action.label" (click)="toggleSearch()">
                <span class="material-icons">{{ action.icon }}</span>
              </button>
            } @else {
              <!-- Other Action Buttons -->
              <button class="sidebar-action" type="button" [attr.aria-label]="action.label">
                <span class="material-icons">{{ action.icon }}</span>
              </button>
            }
          }
        </div>

        <!-- Navigation Menu -->
        <nav class="sidebar-nav">
          <ul class="nav flex-column">
            @for (item of navigationItems; track item.route) {
              <li class="nav-item">
                <a class="nav-link" [routerLink]="[item.route]" routerLinkActive="active">
                  <span class="material-icons me-2">{{ item.icon }}</span>
                  <span>{{ item.label }}</span>
                </a>
              </li>
            }
          </ul>
        </nav>

        <!-- Bottom User Section -->
        <div class="sidebar-bottom">
          <div class="dropdown dropup w-100">
            <a
              class="sidebar-user"
              href="#"
              id="sidebarUserDropdown"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div class="d-flex align-items-center gap-2 w-100">
                @if (me) {
                  @if (me.profilePicture) {
                    <span class="e-avatar e-avatar-circle e-avatar flex-shrink-0">
                      <img [src]="me.profilePicture" [alt]="me.name" [title]="me.name" />
                    </span>
                  } @else {
                    <span
                      class="e-avatar e-avatar-circle e-avatar text-white d-inline-flex justify-content-center align-items-center flex-shrink-0"
                      [title]="me.name"
                    >
                      {{ me.initials }}
                    </span>
                  }
                  <span class="sidebar-username text-truncate flex-grow-1">{{ me.name || 'User' }}</span>
                }
              </div>
            </a>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="sidebarUserDropdown">
              <li>
                <h6 class="dropdown-header text-truncate" style="max-width: 250px;">
                  {{ me?.name || 'User' }}
                </h6>
              </li>
              <li>
                <div class="px-3 py-1 text-muted small text-truncate" style="max-width: 250px;">
                  {{ userEmail || 'Not logged in' }}
                </div>
              </li>
              <li><hr class="dropdown-divider" /></li>

              @for (item of userMenuItems; track item.id) {
                @if (item.action === 'theme-toggle') {
                  <!-- Theme Toggle -->
                  <li>
                    <a class="dropdown-item" href="#" (click)="toggleTheme(); $event.preventDefault()">
                      @if (isDarkMode) {
                        <span class="material-icons me-2">light_mode</span> Switch to Light Mode
                      } @else {
                        <span class="material-icons me-2">dark_mode</span> Switch to Dark Mode
                      }
                    </a>
                  </li>
                } @else if (item.action === 'logout') {
                  <!-- Logout -->
                  <li>
                    <a class="dropdown-item" href="#" (click)="logout($event)">
                      <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                    </a>
                  </li>
                } @else if (item.url) {
                  <!-- External Link -->
                  <li>
                    <a class="dropdown-item" [href]="item.url" target="_blank">
                      <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                    </a>
                  </li>
                } @else {
                  <!-- Regular Menu Item -->
                  <li>
                    <a class="dropdown-item" href="#" (click)="$event.preventDefault()">
                      <span class="material-icons me-2">{{ item.icon }}</span> {{ item.label }}
                    </a>
                  </li>
                }
                @if (item.dividerAfter) {
                  <li><hr class="dropdown-divider" /></li>
                }
              }
            </ul>
          </div>
        </div>
      </aside>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .sidebar {
      position: fixed;
      top: 0;
      left: 0;
      width: var(--cigp-drawer-width);
      height: 100vh;
      background-color: var(--cigp-nav-bg);
      border-right: 1px solid var(--cigp-nav-border);
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      z-index: var(--cigp-z-drawer);
      transition:
        background-color var(--cigp-transition-medium),
        border-color var(--cigp-transition-medium);
    }

    /* Header */
    .sidebar-header {
      min-height: var(--cigp-navbar-height);
      padding: var(--cigp-spacing-base);
      border-bottom: 1px solid var(--cigp-nav-border);
      display: flex;
      align-items: center;
    }

    .sidebar-brand {
      text-decoration: none;
      color: var(--cigp-nav-text);
      font-size: var(--cigp-font-size-lg);
      font-weight: var(--cigp-font-weight-medium);
      display: flex;
      align-items: center;
    }

    .sidebar-brand-text {
      color: var(--cigp-nav-text);
    }

    .sidebar-brand:hover .sidebar-brand-text {
      color: var(--cigp-nav-accent);
    }

    /* Action Buttons Below Header */
    .sidebar-actions {
      padding: var(--cigp-spacing-md);
      border-bottom: 1px solid var(--cigp-nav-border);
      display: flex;
      flex-direction: row;
      gap: var(--cigp-spacing-xs);
      align-items: center;
      justify-content: space-evenly;
    }

    .sidebar-action {
      flex: 0 0 auto;
      padding: var(--cigp-spacing-sm);
      background: transparent;
      border: none;
      color: var(--cigp-nav-accent);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--cigp-radius-base);
      transition: var(--cigp-transition-base);
      position: relative;
    }

    .sidebar-action:hover {
      background-color: rgba(238, 191, 109, 0.1);
      color: var(--cigp-nav-accent);
    }

    .sidebar-action .material-icons {
      font-size: var(--cigp-font-size-icon-base);
    }

    /* Navigation */
    .sidebar-nav {
      flex: 1;
      padding: var(--cigp-spacing-base) 0;
      overflow-y: auto;
    }

    .sidebar-nav .nav {
      padding: 0;
    }

    .sidebar-nav .nav-item {
      margin: 0;
    }

    .sidebar-nav .nav-link {
      padding: var(--cigp-spacing-md) var(--cigp-spacing-base);
      color: var(--cigp-nav-text);
      display: flex;
      align-items: center;
      border-left: 3px solid transparent;
      transition: var(--cigp-transition-base);
      cursor: pointer;
    }

    .sidebar-nav .nav-link:hover {
      background-color: rgba(238, 191, 109, 0.1);
      color: var(--cigp-nav-accent);
    }

    .sidebar-nav .nav-link.active {
      background-color: rgba(238, 191, 109, 0.15);
      color: var(--cigp-nav-accent);
      border-left-color: var(--cigp-nav-accent);
      font-weight: 500;
    }

    .sidebar-nav .material-icons {
      font-size: var(--cigp-font-size-icon-base);
      color: var(--cigp-nav-icon);
    }

    .sidebar-nav .nav-link.active .material-icons {
      color: var(--cigp-nav-accent);
    }

    /* Bottom User Section */
    .sidebar-bottom {
      padding: var(--cigp-spacing-md);
      border-top: 1px solid var(--cigp-nav-border);
    }

    .dropdown.dropup {
      width: 100%;
    }

    .sidebar-action .material-icons {
      font-size: 20px;
    }

    .dropdown.dropup {
      width: 100%;
    }

    .sidebar-user {
      padding: var(--cigp-spacing-sm);
      width: 100%;
      display: flex;
      align-items: center;
      text-decoration: none;
      cursor: pointer;
      border-radius: var(--cigp-radius-base);
      transition: var(--cigp-transition-base);
      color: var(--cigp-nav-text);
    }

    .sidebar-user:hover {
      background-color: rgba(238, 191, 109, 0.1);
      color: var(--cigp-nav-text);
    }

    .sidebar-username {
      color: var(--cigp-nav-text);
      font-size: var(--cigp-font-size-sm);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Avatar */

    .sidebar .e-avatar {
      cursor: pointer;
      border: 2px solid transparent;
      font-size: var(--cigp-font-size-base);
      background-color: var(--cigp-success);
    }

    /* Notification badge */
    .bg-danger {
      background-color: var(--cigp-error) !important;
    }

    /* Scrollbar styling */
    .sidebar::-webkit-scrollbar {
      width: 6px;
    }

    .sidebar::-webkit-scrollbar-track {
      background: transparent;
    }

    .sidebar::-webkit-scrollbar-thumb {
      background: var(--cigp-nav-border);
      border-radius: var(--cigp-radius-sm);
    }

    .sidebar::-webkit-scrollbar-thumb:hover {
      background: var(--cigp-neutral);
    }

    /* Expandable Search Box (Sidebar) */
    .sidebar-search-expanded {
      padding: var(--cigp-spacing-base);
      border-bottom: 1px solid var(--cigp-nav-border);
      animation: expandDown var(--cigp-transition-medium) ease;
      background-color: var(--cigp-nav-bg);
    }

    @keyframes expandDown {
      from {
        opacity: 0;
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
      }
      to {
        opacity: 1;
        max-height: 100px;
        padding-top: var(--cigp-spacing-base);
        padding-bottom: var(--cigp-spacing-base);
      }
    }

    .search-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--cigp-spacing-sm);
    }

    .sidebar-search-input {
      width: 100%;
      background-color: var(--cigp-base-100);
      border: 1px solid var(--cigp-nav-border);
      color: var(--cigp-base-content);
      border-radius: var(--cigp-radius-base);
      padding: var(--cigp-spacing-sm) var(--cigp-spacing-2xl) var(--cigp-spacing-sm) var(--cigp-spacing-md);
      font-size: var(--cigp-font-size-sm);
    }

    .sidebar-search-input:focus {
      outline: none;
      border-color: var(--cigp-nav-accent);
      box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--cigp-primary) 25%, transparent);
    }

    .sidebar-search-input::placeholder {
      color: var(--cigp-neutral-content);
      opacity: 0.6;
    }

    .search-close-btn {
      position: absolute;
      right: var(--cigp-spacing-xs);
      top: 50%;
      transform: translateY(-50%);
      color: var(--cigp-base-content);
      padding: var(--cigp-spacing-xs);
      display: flex;
      align-items: center;
      text-decoration: none;
    }

    .search-close-btn:hover {
      color: var(--cigp-nav-accent);
    }

    .search-close-btn .material-icons {
      font-size: var(--cigp-font-size-icon-base);
    }

    /* Mobile - Hide sidebar on small screens */
    @media (max-width: 991.98px) {
      .sidebar {
        display: none;
      }
    }
  `
})
export class SidebarNavComponent implements OnInit {
  protected me: AvatarCardUser | null = null;
  protected feedbackForm = environment.feedbackForm;
  protected userManual = environment.userManual;
  protected environment = environment.env;
  protected appName = environment.appName;
  protected loggedIn = false;
  protected userEmail: string | null = null;
  protected isDarkMode = false;
  protected searchExpanded = false;
  protected searchQuery = '';

  // Navigation configuration
  protected navigationItems: NavigationItem[] = [];
  protected actionButtons: ActionButton[] = [];
  protected userMenuItems: UserMenuItem[] = [];

  constructor(
    private authService: DeclarationsAuthService,
    private usersService: UsersService,
    private themeService: ThemeService
  ) {}

  public ngOnInit(): void {
    this.authService.loggedIn$.subscribe((loggedIn) => {
      this.loggedIn = loggedIn;
      if (loggedIn) {
        this.usersService.getMyAvatarCardInfo$().subscribe((user) => {
          this.me = user;
          this.userEmail = user?.email || null;
        });

        this.actionButtons = computeActionButtons();
        this.userMenuItems = computeUserMenuItems(environment);
      }
    });

    // Subscribe to theme changes
    this.themeService.theme$.subscribe((theme) => {
      this.isDarkMode = theme === 'dark';
    });
  }

  public logout(event: Event): void {
    event.preventDefault();
    this.authService.logout$().subscribe();
  }

  public toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  public toggleSearch(): void {
    this.searchExpanded = !this.searchExpanded;
    if (!this.searchExpanded) {
      this.searchQuery = '';
    }
  }

  public performSearch(): void {
    if (this.searchQuery.trim()) {
      console.log('Searching for:', this.searchQuery);
      // TODO: Implement actual search functionality
      // e.g., navigate to search results page or filter content
    }
  }
}
